import { useState } from "react";
import { api } from "@/lib/api";
import { CATEGORIES, catLabel, t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Sparkle } from "@phosphor-icons/react";

const empty = { no: "", es: "", en: "", pl: "" };

export function AdminAddWordDialog({ onCreated, lang }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [region, setRegion] = useState("");
  const [collection, setCollection] = useState("no");
  const [sourceLang, setSourceLang] = useState("no");
  const [srcDefinition, setSrcDefinition] = useState("");
  const [srcExample, setSrcExample] = useState("");
  const [definition, setDefinition] = useState({ ...empty });
  const [example, setExample] = useState({ ...empty });
  const [translations, setTranslations] = useState({ ...empty });
  const [generated, setGenerated] = useState(false);

  function reset() {
    setWord("");
    setCategory(CATEGORIES[0]);
    setRegion("");
    setCollection("no");
    setSourceLang("no");
    setSrcDefinition("");
    setSrcExample("");
    setDefinition({ ...empty });
    setExample({ ...empty });
    setTranslations({ ...empty });
    setGenerated(false);
  }

  async function generate() {
    if (!word.trim() || !srcDefinition.trim()) {
      toast.error("Word + definition required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/localize-preview", {
        word,
        definition: srcDefinition,
        example: srcExample,
        source_lang: sourceLang,
        word_lang: collection,
      });
      setDefinition({ ...empty, ...r.data.definition });
      setExample({ ...empty, ...r.data.example });
      setTranslations({ ...empty, ...r.data.translations });
      setGenerated(true);
      toast.success("AI generated");
    } catch (e) {
      toast.error("AI error");
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    setBusy(true);
    try {
      await api.post("/admin/words", {
        word,
        category,
        region,
        source_lang: collection,
        definition,
        example,
        translations,
        status: "approved",
      });
      toast.success("Published");
      setOpen(false);
      reset();
      onCreated();
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  }

  function onOpenChange(v) {
    setOpen(v);
    if (!v) reset();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Button
        data-testid="open-add-word"
        onClick={() => setOpen(true)}
        className="rounded-full bg-[#E85D04] hover:bg-[#DC2F02] text-white"
      >
        <Plus size={16} className="mr-1" /> {t("add_word", lang)}
      </Button>

      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl tracking-tighter">
            {t("add_word_title", lang)}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {t("add_word_hint", lang)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] tracking-eyebrow text-gray-500">Norsk</Label>
              <Input
                data-testid="add-word-input"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                placeholder="faen"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-eyebrow text-gray-500">
                {catLabel(category, lang)}
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="add-word-category" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {catLabel(c, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] tracking-eyebrow text-gray-500">
                {t("region", lang)}
              </Label>
              <Input
                data-testid="add-word-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="all / oslo / bergen"
                className="mt-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-[11px] tracking-eyebrow text-gray-500">
                {t("source_label", lang)}
              </Label>
              <Select value={collection} onValueChange={setCollection}>
                <SelectTrigger data-testid="add-word-collection" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="no">Norsk</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] tracking-eyebrow text-gray-500">
                {t("source_lang", lang)}
              </Label>
              <Select value={sourceLang} onValueChange={setSourceLang}>
                <SelectTrigger data-testid="add-word-source-lang" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="no">NO</SelectItem>
                  <SelectItem value="es">ES</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="pl">PL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2" />
          </div>

          <div>
            <Label className="text-[11px] tracking-eyebrow text-gray-500">
              {t("definition_label", lang)} ({sourceLang.toUpperCase()})
            </Label>
            <Textarea
              data-testid="add-word-src-definition"
              value={srcDefinition}
              onChange={(e) => setSrcDefinition(e.target.value)}
              rows={2}
              className="mt-2 text-sm"
            />
          </div>
          <div>
            <Label className="text-[11px] tracking-eyebrow text-gray-500">
              {t("example_label", lang)} ({sourceLang.toUpperCase()})
            </Label>
            <Textarea
              data-testid="add-word-src-example"
              value={srcExample}
              onChange={(e) => setSrcExample(e.target.value)}
              rows={2}
              className="mt-2 text-sm"
            />
          </div>

          <Button
            data-testid="add-word-generate"
            onClick={generate}
            disabled={busy}
            variant="outline"
            className="rounded-full"
          >
            <Sparkle size={14} className="mr-1" /> {t("generate_ai", lang)}
          </Button>

          {generated && (
            <div className="space-y-5 border-t border-gray-200 pt-5">
              <LocalizedFields
                title={t("definition_label", lang)}
                value={definition}
                onChange={setDefinition}
                testid="def"
              />
              <LocalizedFields
                title={t("example_label", lang)}
                value={example}
                onChange={setExample}
                testid="ex"
              />
              <LocalizedFields
                title={t("translations_label", lang)}
                value={translations}
                onChange={setTranslations}
                testid="tr"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            {t("cancel", lang)}
          </Button>
          <Button
            data-testid="add-word-publish"
            onClick={publish}
            disabled={busy || !generated || !word.trim()}
            className="rounded-full bg-gray-900 hover:bg-gray-800 text-white"
          >
            {t("publish", lang)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LocalizedFields({ title, value, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] tracking-eyebrow text-gray-500 mb-2">{title}</p>
      <div className="space-y-2">
        {["no", "es", "en", "pl"].map((l) => (
          <div key={l} className="flex items-start gap-2">
            <span className="font-mono-code text-[10px] uppercase text-gray-400 pt-2 w-6">
              {l}
            </span>
            <Textarea
              data-testid={`add-word-${testid}-${l}`}
              value={value?.[l] || ""}
              onChange={(e) => onChange({ ...value, [l]: e.target.value })}
              rows={2}
              className="text-sm flex-1"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
